import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppConfig, Scheme, appConfig } from './app.config';

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private readonly STORAGE_KEY = 'avia_theme_config';
    private _config = new BehaviorSubject<AppConfig>(this.loadInitialConfig());
    private _isDark = new BehaviorSubject<boolean>(false);

    constructor() {
        const isAuthPage = typeof window !== 'undefined' && (
            window.location.pathname.includes('sign-in') ||
            window.location.pathname.includes('complete-profile') ||
            window.location.pathname.includes('pending-verification') ||
            window.location.pathname.includes('auth')
        );
        if (!isAuthPage) {
            this.applyConfig(this._config.value);
        } else {
            this.disableThemeForAuth();
        }
    }

    /** Terapkan ulang tema tersimpan ke DOM (dipanggil saat masuk ke layout aplikasi) */
    applyStoredTheme(): void {
        this.applyConfig(this._config.value);
    }

    /** Nonaktifkan sementara kelas dark di DOM (khusus halaman login/auth) */
    disableThemeForAuth(): void {
        const html = document.documentElement;
        const body = document.body;
        html.classList.remove('dark');
        body.classList.remove('dark');
        html.classList.add('light');
        body.classList.add('light');
    }

    /** Observable konfigurasi aplikasi */
    get config$(): Observable<AppConfig> {
        return this._config.asObservable();
    }

    /** Observable status Dark Mode */
    get isDark$(): Observable<boolean> {
        return this._isDark.asObservable();
    }

    /** Status Dark Mode saat ini */
    get isDark(): boolean {
        return this._isDark.value;
    }

    /** Mendapatkan konfigurasi saat ini */
    get currentConfig(): AppConfig {
        return this._config.value;
    }

    /** Ubah mode tampilan ('light' | 'dark') */
    setScheme(scheme: Scheme): void {
        const newConfig: AppConfig = {
            scheme,
        };
        this.saveAndApply(newConfig);
    }

    /** Toggle cepat Light <-> Dark */
    toggleScheme(): void {
        const nextScheme: Scheme = this._isDark.value ? 'light' : 'dark';
        this.setScheme(nextScheme);
    }

    /** Simpan konfigurasi ke localStorage dan terapkan ke DOM */
    private saveAndApply(config: AppConfig): void {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(config));
        } catch (e) {
            console.error('Failed to save theme config to localStorage:', e);
        }
        this._config.next(config);
        this.applyConfig(config);
    }

    /** Terapkan konfigurasi ke DOM */
    private applyConfig(config: AppConfig): void {
        const isDark = config.scheme === 'dark';
        this._isDark.next(isDark);

        const html = document.documentElement;
        const body = document.body;

        if (isDark) {
            html.classList.add('dark');
            body.classList.add('dark');
            html.classList.remove('light');
            body.classList.remove('light');
        } else {
            html.classList.remove('dark');
            body.classList.remove('dark');
            html.classList.add('light');
            body.classList.add('light');
        }
    }

    /** Muat konfigurasi awal dari localStorage atau default */
    private loadInitialConfig(): AppConfig {
        try {
            const saved = localStorage.getItem(this.STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed.scheme === 'dark' || parsed.scheme === 'light') {
                    return { scheme: parsed.scheme };
                }
            }
        } catch (e) {
            console.warn('Failed to load theme config from localStorage:', e);
        }
        return { ...appConfig };
    }
}
